import type { LucideIcon } from 'lucide-react';
import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from 'react';

import type { TransactionStep } from './transaction-sheet';

/** O que os `Fields` de uma ação com step-up dizem ao rodapé e ao trilho do Sheet (W11). */
export interface ActionStepOptions {
  /** A etapa do trilho em que os `Fields` estão: o Resumo do boleto, dentro dos dados, é a Revisão. */
  readonly step?: Extract<TransactionStep, 'data' | 'review'>;
  /** O principal do rodapé. Sem `onSelect`, envia o formulário da ação (validação e, válido, a próxima etapa). */
  readonly primary?: { readonly label: string; readonly onSelect?: () => void; readonly loading?: boolean; readonly disabled?: boolean; readonly icon?: LucideIcon };
  /** Um passo interno dos `Fields` com volta ("Voltar" no rodapé). Ausente, o rodapé mostra "Cancelar". */
  readonly back?: () => void;
  /** `false`: este caminho dos `Fields` vai dos dados direto ao código, sem Revisão no trilho (DARF sem barras). */
  readonly review?: false;
}

interface Host {
  readonly update: (options: ActionStepOptions | null) => void;
}

const ActionStepContext = createContext<Host | null>(null);

/** O hospedeiro (o `ActionDialog` com step-up) lê as opções dos `Fields`; os callbacks vêm sempre da última renderização. */
export function useActionStepHost() {
  const [shape, setShape] = useState<ActionStepOptions | null>(null);
  // Uma caixa estável (não um ref): o clique lê o callback da última renderização dos `Fields`.
  const [store] = useState(() => {
    const latest: { current: ActionStepOptions | null } = { current: null };
    const host: Host = {
      update: (options) => {
        latest.current = options;
        setShape((previous) => (sameShape(previous, options) ? previous : options));
      },
    };
    return { latest, host };
  });
  const primary = shape?.primary;
  return {
    host: store.host,
    step: shape?.step,
    primary: primary
      ? {
          label: primary.label,
          ...(primary.loading === undefined ? {} : { loading: primary.loading }),
          ...(primary.disabled === undefined ? {} : { disabled: primary.disabled }),
          ...(primary.icon ? { icon: primary.icon } : {}),
          ...(primary.onSelect ? { onSelect: () => store.latest.current?.primary?.onSelect?.() } : {}),
        }
      : undefined,
    back: shape?.back ? () => store.latest.current?.back?.() : undefined,
    noReview: shape?.review === false,
  };
}

function sameShape(a: ActionStepOptions | null, b: ActionStepOptions | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.step === b.step &&
    a.primary?.label === b.primary?.label &&
    a.primary?.loading === b.primary?.loading &&
    a.primary?.disabled === b.primary?.disabled &&
    a.primary?.icon === b.primary?.icon &&
    Boolean(a.primary?.onSelect) === Boolean(b.primary?.onSelect) &&
    Boolean(a.back) === Boolean(b.back) &&
    a.review === b.review
  );
}

export function ActionStepProvider({ host, children }: { readonly host: Host; readonly children: ReactNode }) {
  return <ActionStepContext.Provider value={host}>{children}</ActionStepContext.Provider>;
}

/**
 * Nos `Fields` de uma ação com step-up: passos internos (consulta, Resumo do boleto, confirmação da chave) mandam no
 * principal e no "Voltar" do rodapé do Sheet, porque o corpo não tem botão de navegação (W11). Fora do Sheet, não faz nada.
 */
export function useActionStep(options: ActionStepOptions): void {
  const host = useContext(ActionStepContext);
  useLayoutEffect(() => {
    host?.update(options);
  });
  useLayoutEffect(() => () => host?.update(null), [host]);
}
